import * as Clipboard from 'expo-clipboard';
import { Platform } from 'react-native';

export type ClipboardFailureReason =
  | 'unavailable'
  | 'permission-denied'
  | 'link-generation-failed'
  | 'unknown';

export type CopyTextResult =
  | { success: true }
  | { success: false; reason: ClipboardFailureReason };

function getErrorName(error: unknown): string | undefined {
  return typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof (error as { name: unknown }).name === 'string'
    ? (error as { name: string }).name
    : undefined;
}

function getErrorMessage(error: unknown): string {
  return typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
    ? (error as { message: string }).message
    : '';
}

// Maps a failure reason to the i18n key ('shared' namespace) describing it,
// so callers can show something more specific than a generic "Copy Failed".
// `link-generation-failed` isn't produced by copyTextToClipboard itself —
// it's here so callers that build a shareable link *before* copying (e.g.
// a createShareLink mutation returning no URL) can reuse the same
// reason -> message mapping instead of inventing their own.
export const CLIPBOARD_FAILURE_MESSAGE_KEYS: Record<
  ClipboardFailureReason,
  string
> = {
  unavailable: 'shared:copyFailedUnavailable',
  'permission-denied': 'shared:copyFailedPermission',
  'link-generation-failed': 'shared:failedToGenerateLink',
  unknown: 'shared:copyFailed',
};

/**
 * Wraps Clipboard.setStringAsync so permission/API denial never throws
 * unhandled — expo-clipboard rejects on native permission denial but on
 * web silently resolves to `false` instead (TA-10845).
 *
 * On web this deliberately bypasses expo-clipboard's own fallback chain:
 * when `navigator.clipboard.writeText` rejects, expo-clipboard silently
 * retries with the legacy `document.execCommand('copy')`, whose return
 * value only reflects whether the browser *supports* the command — not
 * whether anything actually landed on the system clipboard (e.g. inside a
 * sandboxed iframe, a Permissions-Policy block, or a document that lost
 * focus). That fallback can resolve `true` on a genuine failure, which is
 * why the app showed "Link copied" with nothing copied (TA-10845 web
 * false-positive).
 */
export async function copyTextToClipboard(
  text: string
): Promise<CopyTextResult> {
  if (Platform.OS === 'web') {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return { success: false, reason: 'unavailable' };
    }
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch (error: unknown) {
      // Deliberately does NOT fall back to document.execCommand('copy') on
      // NotAllowedError — that fallback resolves `true` even when nothing
      // was actually copied (e.g. inside a sandboxed iframe, a
      // Permissions-Policy block, or a document that lost focus), which is
      // exactly what caused the app to show "Link copied" with nothing
      // copied (TA-10845 web false-positive). A prior attempt to bring the
      // fallback back (trusting execCommand's own returned boolean) still
      // reproduced the bug in production, so a real denial is reported as
      // a real failure instead.
      if (getErrorName(error) === 'NotAllowedError') {
        return { success: false, reason: 'permission-denied' };
      }
      return { success: false, reason: 'unknown' };
    }
  }

  try {
    // The native module resolves `void` on a successful write in practice,
    // despite its Promise<boolean> type — only an explicit `false` (its web
    // denial-fallback behavior) means the write failed.
    const result = await Clipboard.setStringAsync(text);
    return result === false
      ? { success: false, reason: 'unknown' }
      : { success: true };
  } catch (error: unknown) {
    if (
      getErrorName(error) === 'NoPermissionException' ||
      /permission/i.test(getErrorMessage(error))
    ) {
      return { success: false, reason: 'permission-denied' };
    }
    return { success: false, reason: 'unknown' };
  }
}
